import React from 'react';
import { User, Phone, Fingerprint, Bell, Shield, Archive, Info, Users, ScrollText, AlertTriangle, Server } from 'lucide-react';
import { publicOrigin } from '../../../services/serverBase.mjs';
import { hostLabel } from '../../../services/serverList.mjs';
import { colors, FONT } from '../../../theme';
import { S } from '../dashboardStyles';
import { SS } from './settingsStyles';
import { useSettings } from './SettingsContext';
import NavRow from './NavRow';

/**
 * The root of settings: where everything is, in the order it is used.
 *
 * The order follows one rule — how often somebody comes back to it — and the
 * placement of anything new follows another: what it manages. The two do not
 * argue, because they answer different questions.
 *
 * The exception is the archive, and it is the interesting one. It is opened
 * once in the life of a device, so frequency says "bury it"; but the export is
 * the only thing standing between a phone change and a conversation that
 * exists nowhere, so cost says "raise it". Neither wins: the row stays where
 * frequency puts it and carries a dot while there is no archive. It was a
 * three-line banner above everything; a banner that is always there for a
 * state the row already names is furniture, and furniture is what a person
 * stops reading — including the red one underneath it.
 */
export default function SettingsRoot({ onOpen, onLogout }) {
  const ctx = useSettings();
  const { user, isNative, untrustedCount = 0, archiveNeeded, archiveDate, keyLogAlarm, impostorNotices = [] } = ctx;

  return (
    <div>
      <div style={S.settingsCard}>
        <NavRow icon={User} title="Профиль" value={user?.display_name || '@' + (user?.username || '')}
          onPress={() => onOpen('profile')} last />
      </div>

      {/* Louder than a new device, and above everything else, because it is a
          different kind of statement: not "somebody got a new phone" but "the
          server's own record of who published which key no longer adds up". */}
      {/* Somebody's message arrived over a key that could not be shown to be
          theirs. Not "a new phone appeared" — a handshake that claimed a name
          it could not prove, which is what an impersonating server looks like
          from here. */}
      {impostorNotices.length > 0 && (
        <button style={{ ...SS.hint, background: 'rgba(200,40,40,0.12)' }} onClick={() => onOpen('trust')}>
          <AlertTriangle size={18} color={colors.gray900} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <b>{impostorNotices.length === 1 ? 'Сообщение отклонено' : `Отклонены сообщения от ${impostorNotices.length} собеседников`}.</b>
            {' '}Сменился ключ аккаунта — что делать, в «Ключах и доверии» →
          </span>
        </button>
      )}

      {keyLogAlarm && (
        <button style={{ ...SS.hint, background: 'rgba(200,40,40,0.12)' }} onClick={() => onOpen('trust')}>
          <AlertTriangle size={18} color={colors.gray900} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <b>Журнал ключей не сходится.</b> Сервер изменил запись о том, чей ключ он выдавал —
            так выглядит подмена ключа. Сверьте отпечатки голосом, прежде чем писать. Подробнее →
          </span>
        </button>
      )}

      <div style={S.settingsCard}>
        <NavRow icon={Phone} title="Звонки"
          value={{ e2ee: 'Зашифрованные', sip: 'Обычные', ask: 'Спрашивать' }[ctx.callProtocol] || ''}
          onPress={() => onOpen('calls')} />
        <NavRow icon={Fingerprint} title="Вход и биометрия"
          value={ctx.nativeFaceIdLinked ? 'Face ID' : ''} onPress={() => onOpen('login')} />
        {isNative && (
          <NavRow icon={Bell} title="Уведомления и фон"
            value={ctx.keepAlive ? 'Включено' : 'Выключено'} onPress={() => onOpen('background')} />
        )}
        <NavRow icon={Shield} title="Ключи и доверие"
          badge={untrustedCount > 0 ? String(untrustedCount) : null}
          onPress={() => onOpen('trust')} />
        <NavRow icon={Archive} title="Архив переписки" value={archiveDate || 'не создавался'}
          dot={archiveNeeded ? colors.amber : null} onPress={() => onOpen('archive')} />
        {/* Where the app talks to — visible on every platform, switchable
            only where a shell can restart the UI (ServersScreen says which). */}
        <NavRow icon={Server} title="Сервер и реле" value={hostLabel(publicOrigin())} onPress={() => onOpen('servers')} />
        <NavRow icon={Info} title="О приложении" value={ctx.APP_VERSION} onPress={() => onOpen('about')} last />
      </div>

      {user?.role === 'admin' && (
        <>
          <div style={S.sectionLabel}>Сервер</div>
          <div style={S.settingsCard}>
            <NavRow icon={Users} title="Пользователи" onPress={() => onOpen('admin')} />
            <NavRow icon={ScrollText} title="Журнал действий" onPress={() => onOpen('admin-audit')} last />
          </div>
        </>
      )}

      {/* Below the fold on purpose: nothing else on this screen ends a session. */}
      <div style={SS.dangerZone}>
        <button onClick={onLogout} style={{ ...SS.dangerLink, fontFamily: FONT }}>Выйти</button>
      </div>
    </div>
  );
}
